import { debounce } from '../../shared/functions'
import { getCookie } from '../../shared/cookies'
import { WidgetRuntime } from '../runtime'
import { Event } from './event'
import { EventContext } from './event-context'

export type TrackingSubscriber = (e: Event[], context: EventContext) => void

export type DynamicEventContext = Pick<EventContext, 'recaptcha'>
export class Tracking {
  private events: Event[] = []
  private context: DynamicEventContext = {}
  private readonly processDeb: () => void

  constructor(private runtime: WidgetRuntime, private subscribers: TrackingSubscriber[] = []) {
    this.processDeb = debounce(this.process.bind(this), 250)
  }

  public add(
    event: Event,
    { immediate, context = {} }: { immediate?: boolean; context?: DynamicEventContext } = {},
  ): boolean {
    if (!this.events.some(e => e.key === event.key)) {
      this.events.push(event)
    }
    this.context = { ...this.context, ...context }

    if (immediate) {
      this.process()
    } else {
      this.processDeb()
    }
    return true
  }

  public process(): void {
    const events = this.events.slice()
    const context = this.context
    this.events.length = 0
    this.context = {}
    if (events.length > 0) {
      this.collectContext(context).then(ctx => {
        for (const subscriber of this.subscribers) {
          try {
            subscriber(events, ctx)
          } catch (err) {
            console.error('error calling events subscriber', err)
          }
        }
      })
    }
  }

  private async collectContext(dynamicContext: DynamicEventContext = {}): Promise<EventContext> {
    const visit = this.runtime.session.currentVisit
    const platform = this.runtime.platform
    const now = new Date()

    const context: EventContext = {
      userId: visit.userId,
      sessionId: visit.sessionId,
      siteId: this.runtime.settings.siteId,
      date: now.getTime(),
      visit: {
        utm: {
          medium: visit.utm.utm_medium,
          source: visit.utm.utm_source,
          term: visit.utm.utm_term,
          content: visit.utm.utm_content,
          campaign: visit.utm.utm_campaign,
          method: visit.utm.method,
        },
        source: visit.source,
        referrer: visit.referrer,
        pageNumber: visit.pageNumber,
        sessionNumber: visit.sessionNumber,
        location: visit.location,

        language: visit.language,
        displayWidth: visit.displayWidth,
        displayHeight: visit.displayHeight,

        external: collectExternalData(),
      },
      context: this.runtime.args.getParams(true),
      placement: this.runtime.settings.placement,
      ...dynamicContext,
    }

    if (platform) {
      const platformContext = (await platform.getTrackingContext()) || {}
      context.platform = { type: platform.name, ...platformContext }
    }

    return context
  }
}

function collectExternalData(): { [key: string]: any } {
  const data = {} as { [key: string]: any }

  // HubSpot
  const hubspotutk = getCookie('hubspotutk')
  if (hubspotutk) {
    data['hubspot.utk'] = hubspotutk
  }

  return data
}
